<template>
  <div
    :class="[
      'chart-container-element chart-container-element-' + chartKey,
      { 'popup-border-shadow': isShowChart && metricData.data }
    ]"
    ref="chartContainerRef"
    :style="{ zIndex: getPopupZIndex(chartKey), ...customChartStyle, ...chartStyle }"
  >
    <InsightChart
      class="chart-element"
      ref="chartRef"
      :refreshChartKey="refreshChartKey"
      v-if="isShowChart && metricData.data"
      :options="options"
      :collInterval="collInterval"
      :closeAble="false"
      :indivItemOptions="individualItems"
      :inquiryTime="[startDate, endDate]"
      :data="metricData"
      :paramDownloadChart="paramDownloadChart"
      :isNow="isNow"
      :showLastValue="showLastValue"
      @on-change="handleChartChange"
      @onClose="hideChart"
      @add-widget="(chartType) => addWidgetPopup(selectedMetric, chartType, addWidgetPopupKey)"
      @add-event-policy="() => eventAddModal(selectedMetric, eventPopupKey)"
      @on-zoom="onZoom"
    />
  </div>

  <AddWidgetPopup
    class="chart-overlay"
    :style="{ zIndex: getPopupZIndex(addWidgetPopupKey) }"
    v-if="isShowAddWidgetPopup"
    :searchDate="searchDate"
    mode="externalAdd"
    :config="widgetConfig"
    @close="closeAddWidgetPopup(addWidgetPopupKey)"
    @widgetAdd="onWidgetAdd"
  />

  <EventAddModal
    :style="{ zIndex: getPopupZIndex(eventPopupKey) }"
    class="chart-overlay"
    v-if="isAddEventPolicyPopup && selectedMetric"
    :product-type-code="selectedProductType"
    :subProductCode="selectedSubProduct"
    :selected-target="selectedTargetAddSetting"
    isPerformance
    @close="isAddEventPolicyPopup = false"
    @confirm="closeEventPolicyPopup(eventPopupKey)"
  />
</template>

<script>
import { inject, onMounted, onUnmounted, ref, toRefs, defineComponent, watch } from 'vue';
import widgetAddConfigMixin from '@/containers/widget/configs/widgetAddConfigMixin';
import InsightChart from '@/containers/components/chart/InsightChart';
import EventAddModal from '@/containers/event/eventSettings/EventAddModal.vue';
import AddWidgetPopup from '@/containers/widget/AddWidgetPopup.vue';
import useChartActions from '@/composables/useChartActions';
import { scpmUtil } from '@/utils/scpmUtil';

export default defineComponent({
  mixins: [widgetAddConfigMixin],
  props: {
    wrapper: Object,
    selectedMetric: Object,
    selectedTargets: Array,
    selectedSubProduct: String,
    isShowChart: Boolean,
    chartKey: String,
    metricData: Object,
    individualItems: Array,
    startDate: String,
    endDate: String,
    paramDownloadChart: Object,
    selectedProductType: String,
    collInterval: String,
    options: Object,
    parentClass: String,
    isNow: Boolean,
    showLastValue: { type: Boolean, default: () => true },
    customPopupStyle: {
      type: Object,
      default: () => ({
        alignCenter: true,
        resizeHeight: 0,
        marginTop: 11
      })
    },
    refreshKey: String
  },
  emit: ['update:isShowChart', 'chartChange'],
  components: {
    InsightChart,
    EventAddModal,
    AddWidgetPopup
  },
  setup(props, { emit }) {
    const chartConfig = toRefs(props);
    const eventPopupKey = scpmUtil.generateRandomKey();
    const addWidgetPopupKey = scpmUtil.generateRandomKey();
    const onZoom = inject('onInsightChartZoom');
    const chartContainerRef = ref(null);
    const chartRef = ref(null);
    const chartStyle = ref({});
    const refreshChartKey = ref();
    const customChartStyle = inject('customChartStyle');
    const {
      searchDate,
      widgetConfig,
      isShowAddWidgetPopup,
      isAddEventPolicyPopup,
      selectedTargetAddSetting,
      addWidgetPopup,
      eventAddModal,
      closeAddWidgetPopup,
      closeEventPolicyPopup,
      getPopupZIndex
    } = useChartActions({
      selectedMetric: chartConfig.selectedMetric,
      selectedTargets: chartConfig.selectedTargets,
      selectedProductType: chartConfig.selectedProductType,
      selectedSubProduct: chartConfig.selectedSubProduct,
      start: chartConfig.startDate,
      end: chartConfig.endDate,
      metricData: chartConfig.metricData,
      individualItems: chartConfig.individualItems
    });

    const calculatePositions = () => {
      const targetRect = props.wrapper.getBoundingClientRect();
      const customPopupStyle = props.customPopupStyle;
      const chartElement = chartContainerRef.value;
      const chartRect = chartElement.getBoundingClientRect();
      const parentElement = props.parentClass
        ? document.querySelector(`.${props.parentClass}`)
        : null;
      const parentRect = parentElement ? parentElement.getBoundingClientRect() : null;
      const bodyRect = document.body.getBoundingClientRect();

      const popupWidth = chartRect.width;
      const popupHeight = chartRect.height || 240;

      let anchorStyle = {};
      anchorStyle.left = calculateLeftPosition(
        targetRect,
        chartRect,
        bodyRect,
        popupWidth,
        parentRect,
        customPopupStyle
      );
      anchorStyle.top = calculateTopPosition(
        targetRect,
        chartRect,
        bodyRect,
        popupHeight,
        parentRect,
        customPopupStyle
      );
      chartStyle.value = anchorStyle;
    };

    const calculateLeftPosition = (
      targetRect,
      chartRect,
      bodyRect,
      popupWidth,
      parentRect,
      customPopupStyle
    ) => {
      if (parentRect) {
        return calculateLeftPositionWithParent(
          targetRect,
          chartRect,
          popupWidth,
          parentRect,
          customPopupStyle
        );
      }

      return calculateLeftPositionWithoutParent(targetRect, chartRect, bodyRect, popupWidth);
    };

    const calculateLeftPositionWithParent = (
      targetRect,
      chartRect,
      popupWidth,
      parentRect,
      customPopupStyle
    ) => {
      if (customPopupStyle.alignCenter) {
        const left = (parentRect.width - popupWidth) / 2;
        return `calc(${left}px + ${parentRect.left}px)`;
      }

      const spaceLeft = targetRect.left + targetRect.width / 2 - parentRect.left;
      const spaceRight = parentRect.right - targetRect.right + targetRect.width / 2;

      if (spaceRight >= chartRect.width) {
        return `calc(${targetRect.left}px + ${targetRect.width}px)`;
      }

      if (spaceLeft >= popupWidth / 2) {
        if (spaceRight < popupWidth / 2) {
          return `calc(${parentRect.right}px - ${popupWidth}px)`;
        }
        return `calc(${targetRect.left}px + ${targetRect.width / 2}px - ${popupWidth / 2}px)`;
      }

      return `calc(${parentRect.left}px + 20px)`;
    };

    const calculateLeftPositionWithoutParent = (targetRect, chartRect, bodyRect, popupWidth) => {
      if (
        bodyRect.width - targetRect.left - targetRect.width >= popupWidth / 2 &&
        targetRect.left >= popupWidth / 2
      ) {
        return `calc(${targetRect.left}px + ${targetRect.width / 2}px - ${popupWidth / 2}px)`;
      } else if (targetRect.left >= popupWidth) {
        return `calc(${targetRect.right}px - ${popupWidth}px)`;
      } else {
        const availableSpaceOnRight = bodyRect.width - targetRect.left;
        if (availableSpaceOnRight < popupWidth) {
          return `calc(100vw - ${popupWidth}px - 10px)`;
        } else {
          return `${targetRect.left}px`;
        }
      }
    };

    const calculateTopPosition = (
      targetRect,
      chartRect,
      bodyRect,
      popupHeight,
      parentRect,
      customPopupStyle
    ) => {
      if (parentRect) {
        const spaceBottom = customPopupStyle.alignCenter
          ? parentRect.bottom - targetRect.bottom
          : window.innerHeight - targetRect.bottom;
        if (spaceBottom <= popupHeight + customPopupStyle.resizeHeight) {
          return `calc(${targetRect.top}px - ${popupHeight}px - ${customPopupStyle.marginTop}px)`;
        }
        return `calc(${targetRect.bottom}px + ${customPopupStyle.marginTop}px)`;
      }

      if (bodyRect.height - targetRect.top >= popupHeight + targetRect.height) {
        return `calc(${targetRect.top}px + ${targetRect.height}px)`;
      } else if (targetRect.top >= popupHeight) {
        return `calc(${targetRect.top}px - ${popupHeight}px)`;
      } else {
        return `calc((100vh - ${popupHeight}px) / 2)`;
      }
    };

    onMounted(async () => {
      calculatePositions();
      window.addEventListener('resize', calculatePositions);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', calculatePositions);
    });

    const handleChartChange = (event) => {
      emit('chartChange', event);
    };

    const hideChart = () => {
      emit('update:isShowChart', false);
    };
    const setChartKey = (key) => {
      refreshChartKey.value = key;
    };
    watch(
      () => props.refreshKey,
      (self, prev) => {
        if (self !== prev) {
          setChartKey(self);
        }
      }
    );

    return {
      refreshChartKey,
      chartRef,
      chartContainerRef,
      eventPopupKey,
      addWidgetPopupKey,
      onZoom,
      searchDate,
      widgetConfig,
      isShowAddWidgetPopup,
      isAddEventPolicyPopup,
      selectedTargetAddSetting,
      addWidgetPopup,
      eventAddModal,
      closeAddWidgetPopup,
      closeEventPolicyPopup,
      getPopupZIndex,
      chartStyle,
      handleChartChange,
      hideChart,
      customChartStyle
    };
  }
});
</script>

<style scoped>
.chart-container-element {
  position: fixed !important;
  width: 100% !important;
  border: none !important;
  max-width: 1210px;
  background: white !important;
  border-radius: 4px;
}

.popup-border-shadow {
  box-shadow: 0 8px 15px 2px rgba(34, 37, 42, 0.2), 0 0 4px 0 rgba(34, 37, 42, 0.25),
    0 -1px 4px 0 rgba(34, 37, 42, 0.2);
}

.chart-element {
  width: 100% !important;
  max-width: 1210px !important;
  max-height: 312px !important;
  background: white !important;
  border: none !important;
  border-radius: 4px;
  padding: 12px 24px 24px;
  margin-bottom: 0;
  min-height: 240px;
}
:deep(.chart-container) {
  margin-bottom: 0;
  border: none;
}
</style>
